import {useCallback, useEffect, useState} from 'react';
import {AccountService} from '../../services/AccountService';
import {BoLoginFlow} from '../../services/BoLoginFlow';
import {RouteUtils} from '../../routes/RouteUtils';
import ParamUtil from '../ParamUtil';

/**
 * Handles the legacy -> React handshake when the page is opened with `?token=...`.
 *
 * - `isLoading` stays true until the login is settled: the app must not render (and fire API calls) before.
 * - The one-shot token is exchanged exactly once, even if the layout is mounted several times.
 * - If the exchange fails we go back to the legacy login, unless the redirect loop guard says stop:
 *   then `failed` is set so the layout can show an error instead of bouncing forever.
 */
export function useBoLoginInit() {
    const [isLoading, setIsLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const token = ParamUtil.getParamValue('token');

        if (token !== null) {
            ParamUtil.removeParam('token');

            AccountService.completeBoLogin(token).then(
                () => setIsLoading(false),
                (error) => {
                    console.error('Login failed', error);
                    if (RouteUtils.reboRedirect()) {
                        // Browser is leaving for the legacy: keep the loading overlay, don't fire API calls meanwhile.
                        return;
                    }
                    setFailed(true);
                    setIsLoading(false);
                }
            );
            return;
        }

        // No token in the URL. Either a plain page load, or this layout was remounted while the login that
        // consumed the token is still running: wait for it instead of rendering the app too early.
        const pending = BoLoginFlow.getPending();
        if (pending) {
            pending.then(
                () => setIsLoading(false),
                () => {
                    // the mount that started the login owns the redirect / error handling
                }
            );
        } else {
            setIsLoading(false);
        }
    }, []);

    /** Manual retry from the error screen: start over from the legacy login. */
    const retry = useCallback(() => {
        RouteUtils.resetReboRedirectGuard();
        RouteUtils.reboRedirect();
    }, []);

    return {isLoading, failed, retry};
}
